import { Link } from '@tanstack/react-router';
import { FileText, Upload } from 'lucide-react';
import { useRef, type ReactNode } from 'react';
import {
  ASSESSMENT_ATTEMPT_STATUSES,
  ATTEMPT_STATUS_LABELS,
  INTERVIEW_STATUSES,
  INTERVIEW_STATUS_LABELS,
  type CandidateDocumentRow,
  type CandidateInterviewRow,
  type CandidateProfile,
  type CandidateTestRow,
  type InterviewStatus,
} from '@nest-recruit/shared';
import { Button } from '../../../../components/Button';
import { CvViewer, type CvFile } from '../../../../components/cv/CvViewer';
import { ATTEMPT_STATUS_TONES, INTERVIEW_STATUS_TONES, RESULT_TONES } from '../../../../components/status-tones';
import { ToneChip, type Tone } from '../../../../components/Tone';
import { errorMessage } from '../../../../lib/api-client';
import { displayTimeZone, formatDate, formatTime } from '../../../../lib/format';
import { useCanEdit } from '../../../../stores/session';
import { useToasts } from '../../../../stores/toasts';
import { documentUrl, useUploadCv } from '../../api/candidates-api';
import { fileSize } from '../../candidate-format';
import styles from './CandidatePanel.module.css';

const PDF = /\.pdf$/i;
const WORD = /\.docx?$/i;
const CV_TYPE = 'cv';
const RESCHEDULABLE: readonly InterviewStatus[] = ['slots_offered', 'scheduled', 'confirmed', 'rescheduled'];

/** The CV to show: the newest kept CV, else the newest one removed under retention. */
export function currentCv(documents: readonly CandidateDocumentRow[]): CvFile | null {
  const cvs = documents.filter((document) => document.documentType === CV_TYPE).sort((first, second) => second.uploadedAt.localeCompare(first.uploadedAt));
  const cv = cvs.find((document) => !document.isDeleted) ?? cvs[0];
  if (!cv) return null;
  return { documentId: cv.id, fileName: cv.fileName, kind: cvKindOf(cv.fileName), isRemoved: cv.isDeleted };
}

function cvKindOf(fileName: string): CvFile['kind'] {
  if (PDF.test(fileName)) return 'pdf';
  if (WORD.test(fileName)) return 'word';
  return null;
}

/** The CV, in the app's own viewer: page controls, Fit and Download (SCR-01's viewer). */
export function CvTab({ profile }: { readonly profile: CandidateProfile }): ReactNode {
  return (
    <div className={styles.cvTab}>
      <CvViewer cv={currentCv(profile.documents)} candidateName={profile.candidate.fullName} fallback="Their details are on the left." />
    </div>
  );
}

// --- Interviews --------------------------------------------------------------------------------

function interviewStatusOf(value: string): InterviewStatus | null {
  return INTERVIEW_STATUSES.find((status) => status === value) ?? null;
}

const dayParts = (iso: string): { readonly weekday: string; readonly day: string; readonly month: string } => {
  const parts = new Intl.DateTimeFormat('en-GB', { timeZone: displayTimeZone(), weekday: 'short', day: 'numeric', month: 'short' }).formatToParts(new Date(iso));
  const part = (type: Intl.DateTimeFormatPartTypes): string => parts.find((entry) => entry.type === type)?.value ?? '';
  return { weekday: part('weekday'), day: part('day'), month: part('month') };
};

export function InterviewsTab({ profile }: { readonly profile: CandidateProfile }): ReactNode {
  const canEditInterviews = useCanEdit('interview');
  if (profile.interviews.length === 0) {
    return (
      <div className={styles.tabBody}>
        <div className={styles.empty}>
          <span className={styles.emptyTitle}>No interviews yet</span>
          <span className={styles.emptyBody}>Interviews show here once {firstName(profile.candidate.fullName)} reaches Contact and is planned.</span>
        </div>
      </div>
    );
  }
  return (
    <div className={styles.tabBody}>
      {profile.interviews.map((interview) => (
        <InterviewRow key={interview.id} interview={interview} canEdit={canEditInterviews} />
      ))}
    </div>
  );
}

function InterviewRow({ interview, canEdit }: { readonly interview: CandidateInterviewRow; readonly canEdit: boolean }): ReactNode {
  const status = interviewStatusOf(interview.status);
  const day = interview.startsAt ? dayParts(interview.startsAt) : null;
  const time = interview.startsAt ? `${formatTime(interview.startsAt)}${interview.endsAt ? ` – ${formatTime(interview.endsAt)}` : ''}` : 'Not booked yet';
  const where = [interview.whereText, interview.panelNames.length > 0 ? `Panel: ${interview.panelNames.join(', ')}` : null].filter(Boolean).join(' · ');
  return (
    <article className={styles.recordCard}>
      <div className={styles.dateTile} aria-hidden>
        {day ? (
          <>
            <span className={styles.dateDow}>{day.weekday}</span>
            <span className={styles.dateDay}>{day.day}</span>
            <span className={styles.dateDow}>{day.month}</span>
          </>
        ) : (
          <span className={styles.dateDow}>—</span>
        )}
      </div>
      <div className={styles.recordMain}>
        <div className={styles.recordHead}>
          <span className={styles.recordTime}>{time}</span>
          <ToneChip tone={status ? INTERVIEW_STATUS_TONES[status] : 'new'}>{status ? INTERVIEW_STATUS_LABELS[status] : interview.status}</ToneChip>
          <ToneChip tone="new">{interview.roundLabel}</ToneChip>
        </div>
        <span className={styles.recordTitle}>
          {interview.jobTitle} · {interview.jobReference}
        </span>
        {where && <span className={styles.sub}>{where}</span>}
      </div>
      <div className={styles.recordActions}>
        {canEdit && status && RESCHEDULABLE.includes(status) && (
          <Link to="/interviews/$interviewId/schedule" params={{ interviewId: interview.id }} className={styles.linkButton}>
            Reschedule
          </Link>
        )}
        {status === 'completed' ? (
          <Link to="/interviews/$interviewId/debrief" params={{ interviewId: interview.id }} className={styles.nextStep}>
            Debrief
          </Link>
        ) : (
          <Link to="/interviews/$interviewId" params={{ interviewId: interview.id }} className={styles.linkButton}>
            Open
          </Link>
        )}
      </div>
    </article>
  );
}

// --- Tests -------------------------------------------------------------------------------------

function testResult(test: CandidateTestRow): { readonly tone: Tone; readonly label: string } {
  if (test.isPassed === true) return { tone: RESULT_TONES.passed, label: 'Passed' };
  if (test.isPassed === false) return { tone: RESULT_TONES.below, label: 'Below the pass mark' };
  const status = ASSESSMENT_ATTEMPT_STATUSES.find((value) => value === test.status);
  return status ? { tone: ATTEMPT_STATUS_TONES[status], label: ATTEMPT_STATUS_LABELS[status] } : { tone: 'new', label: test.status };
}

export function TestsTab({ profile }: { readonly profile: CandidateProfile }): ReactNode {
  if (profile.tests.length === 0) {
    return (
      <div className={styles.tabBody}>
        <div className={styles.empty}>
          <span className={styles.emptyTitle}>No tests sent</span>
          <span className={styles.emptyBody}>A test is sent from Contact, when the job has one.</span>
        </div>
      </div>
    );
  }
  return (
    <div className={styles.tabBody}>
      {profile.tests.map((test) => {
        const result = testResult(test);
        const taken = test.takenAt ? `taken ${formatDate(test.takenAt)}${test.minutesTaken === null ? '' : `, ${test.minutesTaken} min`}` : 'not taken yet';
        return (
          <article key={test.id} className={styles.recordCard}>
            <div className={styles.recordMain}>
              <span className={styles.recordTitle}>{test.testName}</span>
              <span className={styles.sub}>
                {test.jobTitle} · {test.isRecordedByHand ? 'Recorded by hand' : 'Online'} · {taken}
              </span>
            </div>
            <div className={styles.testScore}>
              <span className={styles.testFigure}>{test.scorePercent === null ? '—' : `${test.scorePercent}%`}</span>
              {test.passMarkPercent !== null && <span className={styles.sub}>pass mark {test.passMarkPercent}%</span>}
            </div>
            <ToneChip tone={result.tone}>{result.label}</ToneChip>
          </article>
        );
      })}
    </div>
  );
}

// --- Documents ---------------------------------------------------------------------------------

export function DocumentsTab({ profile }: { readonly profile: CandidateProfile }): ReactNode {
  const upload = useUploadCv(profile.candidate.id);
  const show = useToasts((state) => state.show);
  const input = useRef<HTMLInputElement>(null);
  const count = profile.documents.length;

  async function send(file: File): Promise<void> {
    try {
      await upload.mutateAsync({ file, applicationId: null });
      show({ tone: 'success', message: 'CV added' });
    } catch (cause) {
      show({ tone: 'danger', message: "The CV wasn't added", detail: errorMessage(cause) });
    }
  }

  return (
    <div className={styles.tabBody}>
      <div className={styles.tabHeadRow}>
        <span className={styles.tabHeading}>{count === 1 ? '1 file' : `${count} files`}</span>
        <span className={styles.spacer} />
        {profile.can.merge && (
          <>
            <input
              ref={input}
              className={styles.hiddenFile}
              type="file"
              accept=".pdf,.doc,.docx"
              aria-label="Choose a CV file"
              onChange={(event) => {
                const file = event.target.files?.[0];
                event.target.value = '';
                if (file) void send(file);
              }}
            />
            <Button variant="outline" size="sm" icon={Upload} isPending={upload.isPending} onClick={() => input.current?.click()}>
              Add a CV
            </Button>
          </>
        )}
      </div>
      {count === 0 ? (
        <div className={styles.empty}>
          <span className={styles.emptyTitle}>No documents yet</span>
          <span className={styles.emptyBody}>PDF, DOC or DOCX, up to 10 MB.</span>
        </div>
      ) : (
        <ul className={styles.docGrid}>
          {profile.documents.map((document) => (
            <li key={document.id} className={styles.doc}>
              <div className={styles.docThumb} data-gone={document.isDeleted || undefined}>
                {document.isDeleted ? 'Removed under the retention policy' : <FileText size={28} aria-hidden />}
              </div>
              {document.isDeleted ? (
                <span className={styles.docName}>{document.fileName}</span>
              ) : (
                <a className={styles.docName} href={documentUrl(document.id)} target="_blank" rel="noreferrer">
                  {document.fileName}
                </a>
              )}
              <span className={styles.docMeta}>{[document.label, document.applicationReference, fileSize(document.byteSize), formatDate(document.uploadedAt)].filter(Boolean).join(' · ')}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function firstName(fullName: string): string {
  return fullName.trim().split(/\s+/)[0] ?? fullName;
}
